import { Alert, Box, Stack, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { SETTINGS_FIELD_MAX_WIDTH } from "@/design-tokens/layout";
import { shape } from "@/design-tokens/shape";
import { FieldLabel } from "@/features/Settings/components/FieldLabel";
import { StatusDotBadge } from "@/features/Settings/components/StatusDotBadge";
import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import { OnboardingChecklist } from "@/features/ZeroAdmin/onboarding/components/OnboardingChecklist";
import { useOnboardingStatus } from "@/features/ZeroAdmin/onboarding/components/useOnboardingStatus";
import { SiteAutocomplete } from "@/features/ZeroAdmin/sites/SiteAutocomplete";

const searchSchema = z.object({
  site: z.string().optional(),
});

export const Route = createFileRoute(
  "/_auth/settings/$siteId/_zeroAdmin/onboarding/",
)({
  validateSearch: zodValidator(searchSchema),
  component: OnboardingPage,
});

const SHOP_PICKER_ID = "onboarding-site-select";

function OnboardingPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const selectedSite = search.site ?? "";
  const status = useOnboardingStatus(selectedSite);
  const summary = status.summary;
  const allDone =
    summary !== undefined && summary.doneCount === summary.totalCount;

  return (
    <SettingsPageLayout
      breadcrumbs={[{ label: "Settings" }]}
      title="Onboarding"
      titleAdornment={
        summary ? (
          <StatusDotBadge
            label={`${summary.doneCount} of ${summary.totalCount} complete`}
            color={allDone ? "success" : "info"}
            testId="onboarding-progress"
          />
        ) : undefined
      }
      description="See how far along a shop's setup is. Each item is checked automatically from the shop's current settings."
      data-testid="onboarding-page"
    >
      <Stack sx={{ gap: 6 }}>
        <Box sx={{ maxWidth: SETTINGS_FIELD_MAX_WIDTH }}>
          <FieldLabel htmlFor={SHOP_PICKER_ID}>Shop</FieldLabel>
          <SiteAutocomplete
            id={SHOP_PICKER_ID}
            value={selectedSite}
            onChange={(rjId) =>
              void navigate({
                search: (prev) => ({ ...prev, site: rjId || undefined }),
              })
            }
            isConnectOnly
            placeholder="Search shops…"
            testId={SHOP_PICKER_ID}
          />
        </Box>

        {selectedSite ? (
          <OnboardingChecklist status={status} />
        ) : (
          <Box
            sx={{
              py: 12,
              px: 7,
              textAlign: "center",
              borderRadius: `${shape.borderRadius.lg}px`,
              backgroundColor: "background.paper-elevation-1",
            }}
            data-testid="onboarding-no-site"
          >
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              No shop selected
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Search for a shop above to see its onboarding checklist.
            </Typography>
          </Box>
        )}

        <Alert severity="info" data-testid="onboarding-external-note">
          Opt-in popups, audiences and lists, and AI brand creative are managed
          in the main 5050club app and aren&apos;t tracked here yet.
        </Alert>
      </Stack>
    </SettingsPageLayout>
  );
}
